/* eslint-disable require-jsdoc */
import { uuid } from '@hfzq/crab-utils';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { axiosErrorTransformer } from '@src/helper/common/axios-error-transfromer';
import { sha1 } from '@src/helper/common/sha1';
import { CachePool } from '../../helper/cache-pool';
import { getLocalToken, getServerToken } from './request-token';
import { createApiPostCall } from './create-api-call';

/**
 * 接口调用
 *
 * token 自动更新流程
 * - 无token
 * 请求开始之前进入token更新流程，拦截token更新完成前的所有请求
 *
 * - 接口出现401
 * 拦截出现401的接口，当请求队列为空时进入token更新流程
 *
 * - 进入token更新流程后
 * 请求开始之前拦截所有请求
 *
 * - token更新完成
 * 重新发送所有被拦截的请求
 *
 *
 */

const mallRequestInstance = axios.create({
  baseURL: `/lcServer`,
  validateStatus: () => true,
});

interface WaitPoolItem {
  resolve: (token: string) => void;
  reject: (error: { code: number; message: string }) => void;
}
const pendingPool = new CachePool<AxiosRequestConfig>();
const waitPool = new CachePool<WaitPoolItem>();
let isRefreshing = false;

/**
 * 正常情况：无需拦截，请求进入正在请求队列
 * 正在更新token：拦截请求（未发送），将请求保存进待请求队列
 */
mallRequestInstance.interceptors.request.use(async (config) => {
  // 为请求添加请求凭证
  let token: string | null = '';

  // 取消权限验证 将跳过获取token
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!(config as any).cancelAuth) {
    token = await getLocalToken();
  }

  const timestamp = Date.now();
  const requestId = uuid();
  const appKey = process.env.REACT_APP_API_APPKEY;
  const appSecret = process.env.REACT_APP_API_SECRET;
  const sign = sha1(`${appSecret}${timestamp}${requestId}`);

  // eslint-disable-next-line no-param-reassign
  config.headers = {
    'content-type': 'application/json',
    timestamp,
    requestId,
    appKey,
    sign,
  };

  // 取消权限验证 跳过请求拦截过程
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((config as any).cancelAuth) {
    return config;
  }

  // 无token时 开始进入更新token流程
  if (!token) {
    refreshToken();
  }

  // 无token和正在更新token的情况下 拦截本次请求
  if (!token || isRefreshing) {
    //  拦截请求 并将请求添加待请求队列
    return new Promise((resolve, reject) => {
      waitPool.add({
        resolve: (t) => {
          //  更新token完成后 重新将token加入header
          resolve({
            ...config,
            headers: {
              ...config.headers,
              token: t,
            },
          });
        },
        reject,
      });
    });
  }

  pendingPool.add(config);
  return config;
});

/**
 * code 200：正常返回数据
 * code 401（token失效）：进入更新token流程，拦截响应，将请求添加至待请求队列
 * 非code 200: 视为错误，使用reject抛出
 *
 * 更新token：
 * 1、正在请求队列 为空时才会进入更新流程
 * 2、更新流程完成前不再进入
 * 3、更新完成后将待请求队列请求发出，并清空该队列
 *
 */
mallRequestInstance.interceptors.response.use(
  async (response: AxiosResponse) => {
    const { data } = response;
    const requestConfig = response.config;
    pendingPool.remove(requestConfig);

    if (!waitPool.isEmpty()) {
      refreshToken();
    }

    if (response.status === 401) {
      refreshToken();
      //  拦截响应 并将请求重新添加进待请求队列
      return new Promise((resolve, reject) => {
        waitPool.add({
          resolve() {
            resolve(mallRequestInstance(requestConfig));
          },
          reject,
        });
      });
    }

    if (response.status >= 200 && response.status < 300) {
      return data;
    }

    if (response.status === 500) {
      // eslint-disable-next-line no-throw-literal
      throw {
        code: 500,
        message: '系统开小差了，请稍后再试~', // 强制使用此文案
        // detailMessage: data?.detailMessage || data?.message,
      };
    }

    if (data?.message) {
      throw data;
    }

    // eslint-disable-next-line no-throw-literal
    throw {
      code: response.status,
      message: '系统开小差了，请稍后再试~',
      detailMessage: data?.detailMessage,
    };
  },
  (error) => {
    pendingPool.remove(error.config);
    if (!waitPool.isEmpty()) {
      refreshToken();
    }
    axiosErrorTransformer(error);
  }
);

/**
 * 更新token
 */
async function refreshToken() {
  if (pendingPool.isEmpty() && !isRefreshing) {
    isRefreshing = true;
    try {
      const token = await getServerToken();
      // 将待请求队列请求发出
      waitPool.getPool().forEach((subscriber) => {
        subscriber.resolve(token);
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // 所有接口reject
      waitPool.getPool().forEach((subscriber) => {
        subscriber.reject(error);
      });
    }
    // 清空待请求队列
    waitPool.removeAll();
    isRefreshing = false;
  }
}

export interface ApiRequestConfig<T = unknown> extends AxiosRequestConfig<T> {
  autoLoading?: boolean;
}

const apiPost = createApiPostCall(mallRequestInstance);

const apiPostWithoutAuth = createApiPostCall(mallRequestInstance, { cancelAuth: true });

export type RequestParam = unknown;

export type RequestConfig = ApiRequestConfig;

class CommonResource {
  post<T>(url: string, req: RequestParam, config?: RequestConfig): Promise<T> {
    return apiPost(url, req, config);
  }

  postNoAuth<T>(url: string, req: RequestParam, config?: RequestConfig): Promise<T> {
    return apiPostWithoutAuth(url, req, config);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  get<T>(url: string, req: RequestParam, config?: RequestConfig): Promise<any> {
    return Promise.resolve();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  update<T>(url: string, req: RequestParam, config?: RequestConfig): Promise<any> {
    return Promise.resolve();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  delete<T>(url: string, req: RequestParam, config?: RequestConfig): Promise<any> {
    return Promise.resolve();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  put<T>(url: string, req: RequestParam, config?: RequestConfig): Promise<any> {
    return Promise.resolve();
  }
}

export const commonResource = new CommonResource();
